import { PDFDocument, StandardFonts, rgb, PDFPage, PDFFont, PDFTextField, PDFCheckBox } from 'pdf-lib';
import type { TeFormData, TeFormType, GroundType, Title } from '../components/TeFormWizard';

// PDF templates are in the public folder
const te7TemplateUrl = '/TE7.pdf';
const te9TemplateUrl = '/TE9.pdf';

// ============================================================================
// PDF FIELD COORDINATES
// ============================================================================
// These coordinates are based on A4 paper (595 x 842 points)
// Y coordinates are from bottom of page

interface FieldPosition {
  x: number;
  y: number;
  fontSize?: number;
  maxWidth?: number;
  lineHeight?: number;
  uppercase?: boolean;
  baselineAdjust?: number;
}

interface CheckboxPosition {
  x: number;
  y: number;
  size?: number;
}

interface SignaturePosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

// TE9 Witness Statement form layout
const TE9_FIELDS: Record<string, FieldPosition> = {
  // Top section - Case details (from TE3)
  penaltyChargeNumber: { x: 395, y: 720, fontSize: 10 },
  vehicleRegistration: { x: 395, y: 697, fontSize: 10, uppercase: true },
  applicantName: { x: 395, y: 674, fontSize: 10 },
  locationOfContravention: { x: 395, y: 651, fontSize: 10 },
  dateOfContravention: { x: 395, y: 628, fontSize: 10 },

  // Personal details section
  title: { x: 155, y: 558, fontSize: 10 }, // We'll handle title checkboxes separately
  fullName: { x: 155, y: 525, fontSize: 10 },
  companyName: { x: 395, y: 490, fontSize: 9, maxWidth: 170 },
  addressLine1: { x: 85, y: 490, fontSize: 10 },
  addressLine2: { x: 85, y: 470, fontSize: 10 },
  addressLine3: { x: 85, y: 450, fontSize: 10 },
  postcode: { x: 310, y: 420, fontSize: 10, uppercase: true },

  // Payment details (if penalty_paid selected)
  datePaid: { x: 430, y: 308, fontSize: 9 },
  paymentMethod: { x: 430, y: 290, fontSize: 9 },
  paidTo: { x: 430, y: 272, fontSize: 9 },

  // Statement of truth section
  signatureDate: { x: 520, y: 175, fontSize: 10 },
  printedName: { x: 155, y: 140, fontSize: 10 },
};

const TE9_CHECKBOXES: Record<string, CheckboxPosition> = {
  // Title checkboxes
  titleMr: { x: 119, y: 555, size: 10 },
  titleMrs: { x: 170, y: 555, size: 10 },
  titleMiss: { x: 223, y: 555, size: 10 },
  titleMs: { x: 271, y: 555, size: 10 },
  titleOther: { x: 315, y: 555, size: 10 },

  // Ground checkboxes
  did_not_receive_pcn: { x: 70, y: 390, size: 10 },
  made_representations_no_reply: { x: 70, y: 350, size: 10 },
  appealed_no_response: { x: 326, y: 362, size: 10 },
  appealed_not_determined: { x: 326, y: 338, size: 10 },
  appealed_in_favour: { x: 326, y: 318, size: 10 },
  penalty_paid: { x: 306, y: 308, size: 10 },

  // Signing capacity
  signingOfficer: { x: 108, y: 105, size: 10 },
  signingPartner: { x: 199, y: 105, size: 10 },
  signingLitigationFriend: { x: 290, y: 105, size: 10 },
};

const TE9_SIGNATURE: SignaturePosition = {
  x: 90,
  y: 155,
  width: 220,
  height: 45,
};

// TE7 Out of Time Application form layout
const TE7_FIELDS: Record<string, FieldPosition> = {
  // Case details
  penaltyChargeNumber: { x: 395, y: 715, fontSize: 10 },
  vehicleRegistration: { x: 395, y: 692, fontSize: 10, uppercase: true },

  // Personal details
  fullName: { x: 155, y: 545, fontSize: 10 },
  companyName: { x: 395, y: 510, fontSize: 9, maxWidth: 170 },
  addressLine1: { x: 85, y: 510, fontSize: 10 },
  addressLine2: { x: 85, y: 490, fontSize: 10 },
  addressLine3: { x: 85, y: 470, fontSize: 10 },
  postcode: { x: 310, y: 440, fontSize: 10, uppercase: true },

  // Reason for out of time
  outOfTimeReason: { x: 70, y: 375, fontSize: 10, maxWidth: 475, lineHeight: 14 },

  // Statement of truth
  signatureDate: { x: 480, y: 150, fontSize: 10 },
  printedName: { x: 155, y: 118, fontSize: 10 },
};

const TE7_CHECKBOXES: Record<string, CheckboxPosition> = {
  // Title checkboxes
  titleMr: { x: 119, y: 575, size: 10 },
  titleMrs: { x: 170, y: 575, size: 10 },
  titleMiss: { x: 223, y: 575, size: 10 },
  titleMs: { x: 271, y: 575, size: 10 },
  titleOther: { x: 315, y: 575, size: 10 },

  // Signing capacity
  signingOfficer: { x: 108, y: 85, size: 10 },
  signingPartner: { x: 199, y: 85, size: 10 },
  signingLitigationFriend: { x: 290, y: 85, size: 10 },
};

const TE7_SIGNATURE: SignaturePosition = {
  x: 90,
  y: 135,
  width: 220,
  height: 45,
};

// Layout tuning offsets (adjust to align with official PDF templates)
const TE9_X_OFFSET = 0;
const TE9_Y_OFFSET = 0;
const TE9_PERSONAL_Y_ADJUST = 0;
const TE9_GROUNDS_Y_ADJUST = 0;
const TE9_STATEMENT_Y_ADJUST = 0;

const TE7_X_OFFSET = 0;
const TE7_Y_OFFSET = 0;
const TE7_PERSONAL_Y_ADJUST = 0;
const TE7_REASON_Y_ADJUST = 0;
const TE7_STATEMENT_Y_ADJUST = 0;

const applyOffset = <T extends { x: number; y: number }>(
  position: T,
  offsetX: number,
  offsetY: number
): T => ({
  ...position,
  x: position.x + offsetX,
  y: position.y + offsetY,
});

const offsetPositions = <T extends Record<string, { x: number; y: number }>>(
  positions: T,
  offsetX: number,
  offsetY: number
): T => {
  const result: Record<string, any> = {};
  Object.keys(positions).forEach((key) => {
    result[key] = applyOffset(positions[key], offsetX, offsetY);
  });
  return result as T;
};

const TE9_FIELDS_POS = offsetPositions(TE9_FIELDS, TE9_X_OFFSET, TE9_Y_OFFSET);
const TE9_CHECKBOXES_POS = offsetPositions(TE9_CHECKBOXES, TE9_X_OFFSET, TE9_Y_OFFSET);
const TE9_SIGNATURE_POS = applyOffset(TE9_SIGNATURE, TE9_X_OFFSET, TE9_Y_OFFSET);
const TE7_FIELDS_POS = offsetPositions(TE7_FIELDS, TE7_X_OFFSET, TE7_Y_OFFSET);
const TE7_CHECKBOXES_POS = offsetPositions(TE7_CHECKBOXES, TE7_X_OFFSET, TE7_Y_OFFSET);
const TE7_SIGNATURE_POS = applyOffset(TE7_SIGNATURE, TE7_X_OFFSET, TE7_Y_OFFSET);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const wrapText = (text: string, maxWidth: number, font: PDFFont, fontSize: number): string[] => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(testLine, fontSize);

    if (width <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines;
};

const DEFAULT_BASELINE_ADJUST = 0;

const drawText = (
  page: PDFPage,
  text: string,
  position: FieldPosition,
  font: PDFFont
) => {
  if (!text) return;

  const fontSize = position.fontSize || 10;
  const baselineAdjust = position.baselineAdjust ?? DEFAULT_BASELINE_ADJUST;
  const displayText = position.uppercase ? text.toUpperCase() : text;

  if (position.maxWidth) {
    const lines = wrapText(displayText, position.maxWidth, font, fontSize);
    const lineHeight = position.lineHeight || fontSize + 4;

    lines.forEach((line, index) => {
      page.drawText(line, {
        x: position.x,
        y: position.y + baselineAdjust - index * lineHeight,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    });
  } else {
    page.drawText(displayText, {
      x: position.x,
      y: position.y + baselineAdjust,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }
};

const drawCheckmark = (page: PDFPage, position: CheckboxPosition, font: PDFFont) => {
  const size = position.size || 10;
  page.drawText('X', {
    x: position.x,
    y: position.y,
    size,
    font,
    color: rgb(0, 0, 0),
  });
};

const normalizeFieldName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const fieldIncludes = (name: string, terms: string[]) =>
  terms.some((term) => name.includes(term));

const drawDebugRect = (
  page: PDFPage,
  rect: SignaturePosition,
  label: string,
  color: ReturnType<typeof rgb>,
  font: PDFFont
) => {
  page.drawRectangle({
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    borderColor: color,
    borderWidth: 0.6,
  });
  page.drawText(label, {
    x: rect.x + 2,
    y: rect.y + rect.height - 8,
    size: 6,
    font,
    color,
  });
};

const normalizePostcode = (value: string) =>
  value.toUpperCase().replace(/\s+/g, '');

const splitName = (fullName?: string) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const resolveTextFieldValue = (fieldName: string, data: TeFormData, formType: TeFormType): string | null => {
  const name = normalizeFieldName(fieldName);
  if (!name) return null;

  const { firstName, lastName } = splitName(data.fullName || data.printedName);
  const signatureDate = formatDate(data.signatureDate);
  const [sigDay, sigMonth, sigYear] = signatureDate ? signatureDate.split('/') : ['', '', ''];
  const addressLine1 = data.addressLine1 || '';
  const addressLine2 = data.addressLine2 || '';
  const addressLine3 = data.addressLine3 || '';
  const fullAddress = [addressLine1, addressLine2, addressLine3].filter(Boolean).join('\n');
  const isSignatureDateField = isSignatureDateFieldName(name);

  if (fieldIncludes(name, ['penalty charge', 'pcn', 'notice number', 'case number'])) return data.penaltyChargeNumber;
  if (fieldIncludes(name, ['vehicle registration', 'vehicle reg', 'vrm', 'registration'])) return data.vehicleRegistration;
  if (fieldIncludes(name, ['applicant'])) return data.applicantName;
  if (fieldIncludes(name, ['location'])) return data.locationOfContravention;
  if (fieldIncludes(name, ['contravention date', 'date of contravention'])) return formatDate(data.dateOfContravention);
  if (fieldIncludes(name, ['company name']) || (fieldIncludes(name, ['company']) && fieldIncludes(name, ['vehicle', 'registered']))) {
    return data.companyName || '';
  }
  if (fieldIncludes(name, ['address line 1', 'address line1', 'address1', 'address 1', 'address line one'])) return addressLine1;
  if (fieldIncludes(name, ['address line 2', 'address line2', 'address2', 'address 2', 'address line two'])) return addressLine2;
  if (fieldIncludes(name, ['address line 3', 'address line3', 'address3', 'address 3', 'address line three'])) return addressLine3;
  if (fieldIncludes(name, ['address'])) return fullAddress || addressLine1;
  if (fieldIncludes(name, ['city', 'town'])) return addressLine3 || addressLine2 || addressLine1;
  if (fieldIncludes(name, ['postcode', 'post code'])) return data.postcode;
  if (fieldIncludes(name, ['printed name', 'print name'])) return data.printedName;
  if (fieldIncludes(name, ['full name', 'name of witness', 'respondent name', 'witness name', 'name'])) return data.fullName;
  if (fieldIncludes(name, ['first name', 'forename', 'given name'])) return firstName;
  if (fieldIncludes(name, ['last name', 'surname', 'family name'])) return lastName;

  if (fieldIncludes(name, ['date paid', 'paid date', 'payment date']) && data.paymentDetails?.datePaid) {
    return formatDate(data.paymentDetails.datePaid);
  }
  if (fieldIncludes(name, ['paid to', 'payee']) && data.paymentDetails?.paidTo) return data.paymentDetails.paidTo;
  if (fieldIncludes(name, ['payment method']) && data.paymentDetails?.method) return data.paymentDetails.method;

  if (formType === 'te7' && fieldIncludes(name, ['reason', 'reasons', 'explanation', 'out of time', 'statement'])) {
    return data.outOfTimeReason || '';
  }

  if (isSignatureDateField) {
    if (fieldIncludes(name, ['day'])) return sigDay;
    if (fieldIncludes(name, ['month'])) return sigMonth;
    if (fieldIncludes(name, ['year'])) return sigYear;
    if (fieldIncludes(name, ['signature date', 'date signed', 'date'])) return signatureDate;
  }

  return null;
};

const checkboxMatches = (fieldName: string, data: TeFormData) => {
  const name = normalizeFieldName(fieldName);
  if (!name) return false;

  const titleMap: Record<string, Title> = {
    mr: 'Mr',
    mrs: 'Mrs',
    miss: 'Miss',
    ms: 'Ms',
    other: 'Other',
  };

  for (const [key, title] of Object.entries(titleMap)) {
    if (data.title === title && fieldIncludes(name, [key])) return true;
  }

  if (data.signingCapacity === 'officer' && fieldIncludes(name, ['officer'])) return true;
  if (data.signingCapacity === 'partner' && fieldIncludes(name, ['partner'])) return true;
  if (data.signingCapacity === 'litigation_friend' && fieldIncludes(name, ['litigation', 'friend'])) return true;

  const groundTerms: Record<GroundType, string[]> = {
    did_not_receive_pcn: ['did not receive', 'not receive', 'pcn not received'],
    made_representations_no_reply: ['made representations', 'representations', 'no reply', 'rejection notice'],
    appealed_no_response: ['appealed', 'no response', 'no reply'],
    appealed_not_determined: ['appeal', 'not determined', 'not decided'],
    appealed_in_favour: ['appeal', 'in my favour', 'in my favor', 'successful'],
    penalty_paid: ['penalty paid', 'paid in full', 'paid'],
  };

  return data.grounds.some((ground) => fieldIncludes(name, groundTerms[ground]));
};

const getFieldRect = (field: any) => {
  try {
    const widgets = field?.acroField?.getWidgets?.() || [];
    if (!widgets.length) return null;
    const rect = widgets[0]?.getRectangle?.();
    if (!rect) return null;
    if (Array.isArray(rect)) {
      const [x1, y1, x2, y2] = rect;
      return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
    }
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
  } catch {
    return null;
  }
};

const getSignaturePositionFromField = (field: any): SignaturePosition | null => {
  const rect = getFieldRect(field);
  if (!rect) return null;
  return rect;
};

const isSignatureFieldName = (name: string) => {
  if (!fieldIncludes(name, ['signature', 'signed', 'sig'])) return false;
  if (fieldIncludes(name, ['date', 'dated', 'day', 'month', 'year'])) return false;
  if (fieldIncludes(name, ['print', 'printed'])) return false;
  return true;
};

const scoreSignatureCandidate = (candidate: { rect: SignaturePosition; name: string }) => {
  const { rect, name } = candidate;
  const width = rect.width || 0;
  const height = rect.height || 0.0001;
  const wideRatio = width / height;
  const lowerBias = Math.max(0, 500 - rect.y);
  let score = wideRatio * 10 + width * 0.2 + lowerBias * 0.05;
  if (fieldIncludes(name, ['signature', 'signed'])) score += 40;
  if (fieldIncludes(name, ['sig'])) score += 10;
  if (width >= 160) score += 15;
  if (height >= 10 && height <= 40) score += 10;
  return score;
};

const selectSignaturePosition = (candidates: Array<{ rect: SignaturePosition; name: string }>) => {
  if (!candidates.length) return null;
  const strict = candidates.filter((candidate) => isSignatureFieldName(candidate.name));
  if (!strict.length) return null;
  const viable = strict.filter((candidate) =>
    candidate.rect.width >= 140 &&
    candidate.rect.height >= 12 &&
    candidate.rect.height <= 60
  );
  const ranked = (viable.length ? viable : [])
    .slice()
    .sort((a, b) => scoreSignatureCandidate(b) - scoreSignatureCandidate(a));
  return ranked[0]?.rect || null;
};

const isPrintedNameFieldName = (name: string) => {
  if (!fieldIncludes(name, ['print', 'printed'])) return false;
  if (!fieldIncludes(name, ['name'])) return false;
  return true;
};

const pickSignatureDateCandidate = (candidates: SignaturePosition[]) => {
  if (!candidates.length) return null;
  return candidates
    .slice()
    .sort((a, b) => b.x - a.x)[0];
};

const pickPrintedNameCandidate = (candidates: SignaturePosition[]) => {
  if (!candidates.length) return null;
  return candidates
    .slice()
    .sort((a, b) => a.y - b.y)[0];
};

const deriveSignaturePosition = (
  signatureDateCandidate: SignaturePosition | null,
  printedNameCandidate: SignaturePosition | null,
  fallback: SignaturePosition,
  formType: TeFormType
) => {
  if (!signatureDateCandidate && !printedNameCandidate) return null;
  const width = fallback.width;
  const height = fallback.height;
  const gap = 10;
  const x = signatureDateCandidate
    ? Math.max(20, signatureDateCandidate.x - width - gap)
    : fallback.x;
  let y = fallback.y;
  if (printedNameCandidate) {
    const offset = formType === 'te7' ? 17 : 15;
    y = printedNameCandidate.y + offset;
  } else if (signatureDateCandidate) {
    y = signatureDateCandidate.y - 5;
  }
  return {
    x,
    y,
    width,
    height,
  };
};

const isSignaturePositionPlausible = (
  candidate: SignaturePosition | null,
  fallback: SignaturePosition
) => {
  if (!candidate) return false;
  const maxDeltaX = 140;
  const maxDeltaY = 120;
  return (
    Math.abs(candidate.x - fallback.x) <= maxDeltaX &&
    Math.abs(candidate.y - fallback.y) <= maxDeltaY
  );
};

const clampSignaturePosition = (
  position: SignaturePosition,
  page: PDFPage
): SignaturePosition => {
  const { width: pageWidth, height: pageHeight } = page.getSize();
  const width = Math.min(position.width, pageWidth);
  const height = Math.min(position.height, pageHeight);
  const x = Math.min(Math.max(0, position.x), pageWidth - width);
  const y = Math.min(Math.max(0, position.y), pageHeight - height);
  return { x, y, width, height };
};

const isSignatureDateFieldName = (name: string) => {
  if (!fieldIncludes(name, ['date'])) return false;
  if (fieldIncludes(name, ['contravention', 'paid', 'payment', 'issue', 'service', 'pcn', 'notice'])) return false;
  if (fieldIncludes(name, ['signed', 'signature', 'statement', 'application', 'witness', 'submission', 'today'])) return true;
  return /\bdate\s*\d+\b/.test(name) || name === 'date';
};

const buildDateSegments = (fields: PDFTextField[], signatureDate: string) => {
  const numeric = signatureDate.replace(/\D/g, '');
  const map = new Map<PDFTextField, string>();
  if (!numeric) return map;

  const matches = fields
    .map((field) => {
      const name = normalizeFieldName(field.getName());
      if (!isSignatureDateFieldName(name)) return null;
      const maxLength = field.getMaxLength?.() || numeric.length;
      const indexMatch = name.match(/date\s*(\d+)/);
      const index = indexMatch ? Number(indexMatch[1]) : null;
      const rect = getFieldRect(field);
      return { field, maxLength: Math.max(1, maxLength), index, x: rect?.x ?? 0 };
    })
    .filter(Boolean) as Array<{ field: PDFTextField; maxLength: number; index: number | null; x: number }>;

  const smallFields = matches.filter((m) => m.maxLength <= 4);
  if (smallFields.length < 2) return map;

  const ordered = smallFields.sort((a, b) => {
    if (a.index !== null && b.index !== null) return a.index - b.index;
    if (a.index !== null) return -1;
    if (b.index !== null) return 1;
    return a.x - b.x;
  });

  let cursor = 0;
  ordered.forEach(({ field, maxLength }) => {
    const segment = numeric.slice(cursor, cursor + maxLength);
    map.set(field, segment);
    cursor += maxLength;
  });

  return map;
};

const buildPostcodeSegments = (fields: PDFTextField[], postcode: string) => {
  const normalized = normalizePostcode(postcode || '');
  const map = new Map<PDFTextField, string>();
  if (!normalized) return map;

  const matches = fields
    .map((field) => {
      const name = normalizeFieldName(field.getName());
      if (!fieldIncludes(name, ['postcode', 'post code'])) return null;
      const maxLength = field.getMaxLength?.() || normalized.length;
      const indexMatch = name.match(/postcode\s*(\d+)/);
      const index = indexMatch ? Number(indexMatch[1]) : null;
      const rect = getFieldRect(field);
      return { field, maxLength: Math.max(1, maxLength), index, x: rect?.x ?? 0 };
    })
    .filter(Boolean) as Array<{ field: PDFTextField; maxLength: number; index: number | null; x: number }>;

  if (!matches.length) return map;

  const ordered = matches.sort((a, b) => {
    if (a.index !== null && b.index !== null) return a.index - b.index;
    if (a.index !== null) return -1;
    if (b.index !== null) return 1;
    return a.x - b.x;
  });

  let cursor = 0;
  ordered.forEach(({ field, maxLength }) => {
    const segment = normalized.slice(cursor, cursor + maxLength);
    map.set(field, segment);
    cursor += maxLength;
  });

  return map;
};

const fitValueToField = (
  field: PDFTextField,
  _fieldName: string,
  value: string,
  postcodeSegments: Map<PDFTextField, string>,
  dateSegments: Map<PDFTextField, string>
) => {
  const postcodeSegment = postcodeSegments.get(field);
  if (postcodeSegment !== undefined) return postcodeSegment;
  const dateSegment = dateSegments.get(field);
  if (dateSegment !== undefined) return dateSegment;

  const maxLength = field.getMaxLength?.();
  if (typeof maxLength === 'number' && maxLength > 0 && value.length > maxLength) {
    return value.slice(0, maxLength);
  }
  return value;
};

const fillTeFormFields = (
  pdfDoc: PDFDocument,
  data: TeFormData,
  formType: TeFormType,
  font: PDFFont
) => {
  const form = pdfDoc.getForm();
  const fields = form.getFields();
  const textFields = fields.filter((field): field is PDFTextField => field instanceof PDFTextField);
  const postcodeSegments = buildPostcodeSegments(textFields, data.postcode);
  const signatureDate = formatDate(data.signatureDate);
  const dateSegments = buildDateSegments(textFields, signatureDate);
  let filledText = 0;
  let checkedBoxes = 0;
  const signatureCandidates: Array<{ rect: SignaturePosition; name: string }> = [];
  const signatureDateCandidates: SignaturePosition[] = [];
  const printedNameCandidates: SignaturePosition[] = [];
  const signatureAnchorMaxY = 260;

  fields.forEach((field) => {
    const fieldName = field.getName();
    const normalized = normalizeFieldName(fieldName);

    if (fieldIncludes(normalized, ['signature', 'signed', 'sig'])) {
      const candidate = getSignaturePositionFromField(field);
      if (candidate && candidate.x < 320 && candidate.width >= 120 && candidate.height >= 12 && candidate.height <= 80) {
        signatureCandidates.push({ rect: candidate, name: normalized });
      }
    }

    if (field instanceof PDFTextField) {
      const rect = getFieldRect(field);
      if (rect && rect.y <= signatureAnchorMaxY) {
        if (isSignatureDateFieldName(normalized)) signatureDateCandidates.push(rect);
        if (isPrintedNameFieldName(normalized)) printedNameCandidates.push(rect);
      }
      const value = resolveTextFieldValue(fieldName, data, formType);
      if (value) {
        const fitted = fitValueToField(field, fieldName, value, postcodeSegments, dateSegments);
        if (!fitted) return;
        field.setText(fitted);
        filledText += 1;
      }
      return;
    }

    if (field instanceof PDFCheckBox) {
      if (checkboxMatches(fieldName, data)) {
        field.check();
        checkedBoxes += 1;
      }
    }
  });

  if (filledText > 0) {
    form.updateFieldAppearances(font);
  }

  const explicitSignaturePosition = selectSignaturePosition(signatureCandidates);
  const signatureDateAnchor = pickSignatureDateCandidate(signatureDateCandidates);
  const printedNameAnchor = pickPrintedNameCandidate(printedNameCandidates);
  const fallbackSignature = formType === 'te7' ? TE7_SIGNATURE_POS : TE9_SIGNATURE_POS;
  const anchoredSignaturePosition = deriveSignaturePosition(
    signatureDateAnchor,
    printedNameAnchor,
    fallbackSignature,
    formType
  );
  const signaturePosition = explicitSignaturePosition || anchoredSignaturePosition || null;

  return { filledText, checkedBoxes, signaturePosition };
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const decodeDataUrl = (dataUrl: string): Uint8Array => {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const trimSignatureDataUrl = (dataUrl: string): Promise<string> => {
  if (typeof document === 'undefined') return Promise.resolve(dataUrl);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (!width || !height) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      let minX = width;
      let minY = height;
      let maxX = 0;
      let maxY = 0;
      let found = false;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          const alpha = data[idx + 3];
          if (alpha > 10) {
            found = true;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (!found) {
        resolve(dataUrl);
        return;
      }
      const trimWidth = Math.max(1, maxX - minX + 1);
      const trimHeight = Math.max(1, maxY - minY + 1);
      const outCanvas = document.createElement('canvas');
      outCanvas.width = trimWidth;
      outCanvas.height = trimHeight;
      const outCtx = outCanvas.getContext('2d');
      if (!outCtx) {
        resolve(dataUrl);
        return;
      }
      outCtx.drawImage(canvas, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);
      resolve(outCanvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

const drawSignatureDebugOverlay = (
  pdfDoc: PDFDocument,
  page: PDFPage,
  font: PDFFont,
  signatureTarget: SignaturePosition,
  fallbackSignature: SignaturePosition
) => {
  const form = pdfDoc.getForm();
  const fields = form.getFields();
  const signatureFields: SignaturePosition[] = [];
  const signatureDateFields: SignaturePosition[] = [];
  const printedNameFields: SignaturePosition[] = [];

  fields.forEach((field) => {
    const name = normalizeFieldName(field.getName());
    const rect = getFieldRect(field);
    if (!rect) return;
    if (isSignatureFieldName(name)) signatureFields.push(rect);
    if (isSignatureDateFieldName(name)) signatureDateFields.push(rect);
    if (isPrintedNameFieldName(name)) printedNameFields.push(rect);
  });

  signatureFields.forEach((rect) => drawDebugRect(page, rect, 'SIG', rgb(0.85, 0.2, 0.2), font));
  signatureDateFields.forEach((rect) => drawDebugRect(page, rect, 'DATE', rgb(0.15, 0.4, 0.9), font));
  printedNameFields.forEach((rect) => drawDebugRect(page, rect, 'PRINT', rgb(0.1, 0.65, 0.25), font));
  drawDebugRect(page, fallbackSignature, 'SIG_BOX', rgb(0.75, 0.1, 0.75), font);
  drawDebugRect(page, signatureTarget, 'SIG_TARGET', rgb(0.95, 0.55, 0.1), font);
};

const drawSignature = async (
  pdfDoc: PDFDocument,
  page: PDFPage,
  position: SignaturePosition,
  dataUrl?: string
) => {
  if (!dataUrl) return;
  const trimmedDataUrl = await trimSignatureDataUrl(dataUrl);
  const bytes = decodeDataUrl(trimmedDataUrl);
  const image = trimmedDataUrl.startsWith('data:image/jpeg') || trimmedDataUrl.startsWith('data:image/jpg')
    ? await pdfDoc.embedJpg(bytes)
    : await pdfDoc.embedPng(bytes);
  const size = image.scale(1);
  const scale = Math.min(position.width / size.width, position.height / size.height);
  const drawWidth = size.width * scale;
  const drawHeight = size.height * scale;
  const offsetX = position.x + (position.width - drawWidth) / 2;
  const offsetY = position.y + (position.height - drawHeight) / 2;
  page.drawImage(image, {
    x: offsetX,
    y: offsetY,
    width: drawWidth,
    height: drawHeight,
  });
};

const resolvePdfUrl = (url: string) => {
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const normalized = url.startsWith('/') ? url.slice(1) : url;
  const baseHref = typeof document !== 'undefined'
    ? document.querySelector('base')?.href
    : undefined;
  if (baseHref) return new URL(normalized, baseHref).toString();
  const base = (import.meta as any)?.env?.BASE_URL || '/';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const normalizedBase = base.startsWith('/') ? base : `/${base}`;
  if (origin) {
    const baseUrl = new URL(normalizedBase.endsWith('/') ? normalizedBase : `${normalizedBase}/`, origin);
    return new URL(normalized, baseUrl).toString();
  }
  return `${normalizedBase}${normalized}`;
};

const fetchPdfTemplate = async (path: string): Promise<Uint8Array> => {
  if (path.startsWith('data:')) {
    return decodeDataUrl(path);
  }
  const resolvedUrl = resolvePdfUrl(path);
  const candidates = new Set<string>([resolvedUrl, path]);
  if (typeof window !== 'undefined') {
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    candidates.add(new URL(normalized, `${window.location.origin}/`).toString());
  }

  const isPdfBytes = (bytes: Uint8Array) => {
    const head = bytes.slice(0, Math.min(1024, bytes.length));
    let text = '';
    for (let i = 0; i < head.length; i += 1) {
      text += String.fromCharCode(head[i]);
    }
    return text.includes('%PDF-');
  };

  let lastError = '';
  for (const candidate of candidates) {
    const response = await fetch(candidate, { cache: 'no-store' });
    if (!response.ok) {
      lastError = `Failed to fetch template (${response.status}) from ${candidate}`;
      continue;
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (!bytes.length) {
      lastError = `Empty response from ${candidate}`;
      continue;
    }
    if (!isPdfBytes(bytes)) {
      const preview = new TextDecoder('utf-8')
        .decode(bytes.slice(0, 200))
        .replace(/\s+/g, ' ')
        .trim();
      lastError = `Non-PDF response from ${candidate}. Preview: "${preview.slice(0, 120)}"`;
      continue;
    }
    return bytes;
  }

  throw new Error(lastError || `Failed to load PDF template from ${path}`);
};

// ============================================================================
// PDF GENERATION FUNCTIONS
// ============================================================================

export const generateTE9Pdf = async (
  data: TeFormData,
  options?: { debug?: boolean }
): Promise<Uint8Array> => {
  // Fetch the blank TE9 form
  const existingPdfBytes = await fetchPdfTemplate(te9TemplateUrl);
  let pdfDoc: PDFDocument;
  try {
    pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });
  } catch (err: any) {
    throw new Error(`Failed to parse TE9 template: ${err?.message || err}`);
  }
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();
  const page = pages[0];
  const { filledText, checkedBoxes, signaturePosition } = fillTeFormFields(pdfDoc, data, 'te9', font);
  const useOverlay = filledText === 0 && checkedBoxes === 0;

  if (useOverlay) {
    // Fill case details
    drawText(page, data.penaltyChargeNumber, TE9_FIELDS_POS.penaltyChargeNumber, font);
    drawText(page, data.vehicleRegistration, TE9_FIELDS_POS.vehicleRegistration, font);
    drawText(page, data.applicantName, TE9_FIELDS_POS.applicantName, font);
    drawText(page, data.locationOfContravention, TE9_FIELDS_POS.locationOfContravention, font);
    drawText(page, formatDate(data.dateOfContravention), TE9_FIELDS_POS.dateOfContravention, font);

    // Fill title checkbox
    const titleMap: Record<string, string> = {
      Mr: 'titleMr',
      Mrs: 'titleMrs',
      Miss: 'titleMiss',
      Ms: 'titleMs',
      Other: 'titleOther',
    };
    const titleCheckbox = titleMap[data.title];
    if (titleCheckbox && TE9_CHECKBOXES_POS[titleCheckbox]) {
      drawCheckmark(page, applyOffset(TE9_CHECKBOXES_POS[titleCheckbox], 0, TE9_PERSONAL_Y_ADJUST), font);
    }
    if (data.title === 'Other' && data.otherTitle) {
      drawText(
        page,
        data.otherTitle,
        applyOffset({ x: 350, y: 555, fontSize: 10 }, TE9_X_OFFSET, TE9_Y_OFFSET + TE9_PERSONAL_Y_ADJUST),
        font
      );
    }

    // Fill personal details
    drawText(page, data.fullName, applyOffset(TE9_FIELDS_POS.fullName, 0, TE9_PERSONAL_Y_ADJUST), font);
    if (data.companyName) {
      drawText(page, data.companyName, applyOffset(TE9_FIELDS_POS.companyName, 0, TE9_PERSONAL_Y_ADJUST), font);
    }
    drawText(page, data.addressLine1, applyOffset(TE9_FIELDS_POS.addressLine1, 0, TE9_PERSONAL_Y_ADJUST), font);
    if (data.addressLine2) {
      drawText(page, data.addressLine2, applyOffset(TE9_FIELDS_POS.addressLine2, 0, TE9_PERSONAL_Y_ADJUST), font);
    }
    if (data.addressLine3) {
      drawText(page, data.addressLine3, applyOffset(TE9_FIELDS_POS.addressLine3, 0, TE9_PERSONAL_Y_ADJUST), font);
    }
    drawText(page, data.postcode, applyOffset(TE9_FIELDS_POS.postcode, 0, TE9_PERSONAL_Y_ADJUST), font);

    // Fill ground checkboxes
    data.grounds.forEach((ground) => {
      const checkboxPos = TE9_CHECKBOXES_POS[ground];
      if (checkboxPos) {
        drawCheckmark(page, applyOffset(checkboxPos, 0, TE9_GROUNDS_Y_ADJUST), font);
      }
    });

    // Fill payment details if applicable
    if (data.grounds.includes('penalty_paid') && data.paymentDetails) {
      drawText(page, formatDate(data.paymentDetails.datePaid), applyOffset(TE9_FIELDS_POS.datePaid, 0, TE9_GROUNDS_Y_ADJUST), font);
      const methodLabels: Record<string, string> = {
        cash: 'Cash',
        cheque: 'Cheque',
        debit: 'Debit card',
        credit: 'Credit card',
      };
      drawText(page, methodLabels[data.paymentDetails.method] || '', applyOffset(TE9_FIELDS_POS.paymentMethod, 0, TE9_GROUNDS_Y_ADJUST), font);
      drawText(page, data.paymentDetails.paidTo, applyOffset(TE9_FIELDS_POS.paidTo, 0, TE9_GROUNDS_Y_ADJUST), font);
    }

    // Fill statement of truth
    drawText(page, formatDate(data.signatureDate), applyOffset(TE9_FIELDS_POS.signatureDate, 0, TE9_STATEMENT_Y_ADJUST), font);
    drawText(page, data.printedName, applyOffset(TE9_FIELDS_POS.printedName, 0, TE9_STATEMENT_Y_ADJUST), font);

    // Fill signing capacity checkboxes (if not signing as witness)
    if (data.signingCapacity === 'officer') {
      drawCheckmark(page, applyOffset(TE9_CHECKBOXES_POS.signingOfficer, 0, TE9_STATEMENT_Y_ADJUST), font);
    } else if (data.signingCapacity === 'partner') {
      drawCheckmark(page, applyOffset(TE9_CHECKBOXES_POS.signingPartner, 0, TE9_STATEMENT_Y_ADJUST), font);
    } else if (data.signingCapacity === 'litigation_friend') {
      drawCheckmark(page, applyOffset(TE9_CHECKBOXES_POS.signingLitigationFriend, 0, TE9_STATEMENT_Y_ADJUST), font);
    }
  }

  const fallbackSignature = applyOffset(TE9_SIGNATURE_POS, 0, TE9_STATEMENT_Y_ADJUST);
  const signatureTarget = isSignaturePositionPlausible(signaturePosition, fallbackSignature)
    ? signaturePosition!
    : fallbackSignature;
  await drawSignature(pdfDoc, page, clampSignaturePosition(signatureTarget, page), data.signatureDataUrl);
  if (options?.debug) {
    drawSignatureDebugOverlay(pdfDoc, page, font, clampSignaturePosition(signatureTarget, page), fallbackSignature);
  }

  return pdfDoc.save();
};

export const generateTE7Pdf = async (
  data: TeFormData,
  options?: { debug?: boolean }
): Promise<Uint8Array> => {
  // Fetch the blank TE7 form
  const existingPdfBytes = await fetchPdfTemplate(te7TemplateUrl);
  let pdfDoc: PDFDocument;
  try {
    pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });
  } catch (err: any) {
    throw new Error(`Failed to parse TE7 template: ${err?.message || err}`);
  }
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();
  const page = pages[0];
  const { filledText, checkedBoxes, signaturePosition } = fillTeFormFields(pdfDoc, data, 'te7', font);
  const useOverlay = filledText === 0 && checkedBoxes === 0;

  if (useOverlay) {
    // Fill case details
    drawText(page, data.penaltyChargeNumber, TE7_FIELDS_POS.penaltyChargeNumber, font);
    drawText(page, data.vehicleRegistration, TE7_FIELDS_POS.vehicleRegistration, font);

    // Fill title checkbox
    const titleMap: Record<string, string> = {
      Mr: 'titleMr',
      Mrs: 'titleMrs',
      Miss: 'titleMiss',
      Ms: 'titleMs',
      Other: 'titleOther',
    };
    const titleCheckbox = titleMap[data.title];
    if (titleCheckbox && TE7_CHECKBOXES_POS[titleCheckbox]) {
      drawCheckmark(page, applyOffset(TE7_CHECKBOXES_POS[titleCheckbox], 0, TE7_PERSONAL_Y_ADJUST), font);
    }
    if (data.title === 'Other' && data.otherTitle) {
      drawText(
        page,
        data.otherTitle,
        applyOffset({ x: 350, y: 575, fontSize: 10 }, TE7_X_OFFSET, TE7_Y_OFFSET + TE7_PERSONAL_Y_ADJUST),
        font
      );
    }

    // Fill personal details
    drawText(page, data.fullName, applyOffset(TE7_FIELDS_POS.fullName, 0, TE7_PERSONAL_Y_ADJUST), font);
    if (data.companyName) {
      drawText(page, data.companyName, applyOffset(TE7_FIELDS_POS.companyName, 0, TE7_PERSONAL_Y_ADJUST), font);
    }
    drawText(page, data.addressLine1, applyOffset(TE7_FIELDS_POS.addressLine1, 0, TE7_PERSONAL_Y_ADJUST), font);
    if (data.addressLine2) {
      drawText(page, data.addressLine2, applyOffset(TE7_FIELDS_POS.addressLine2, 0, TE7_PERSONAL_Y_ADJUST), font);
    }
    if (data.addressLine3) {
      drawText(page, data.addressLine3, applyOffset(TE7_FIELDS_POS.addressLine3, 0, TE7_PERSONAL_Y_ADJUST), font);
    }
    drawText(page, data.postcode, applyOffset(TE7_FIELDS_POS.postcode, 0, TE7_PERSONAL_Y_ADJUST), font);

    // Fill out of time reason
    if (data.outOfTimeReason) {
      drawText(page, data.outOfTimeReason, applyOffset(TE7_FIELDS_POS.outOfTimeReason, 0, TE7_REASON_Y_ADJUST), font);
    }

    // Fill statement of truth
    drawText(page, formatDate(data.signatureDate), applyOffset(TE7_FIELDS_POS.signatureDate, 0, TE7_STATEMENT_Y_ADJUST), font);
    drawText(page, data.printedName, applyOffset(TE7_FIELDS_POS.printedName, 0, TE7_STATEMENT_Y_ADJUST), font);

    // Fill signing capacity checkboxes
    if (data.signingCapacity === 'officer') {
      drawCheckmark(page, applyOffset(TE7_CHECKBOXES_POS.signingOfficer, 0, TE7_STATEMENT_Y_ADJUST), font);
    } else if (data.signingCapacity === 'partner') {
      drawCheckmark(page, applyOffset(TE7_CHECKBOXES_POS.signingPartner, 0, TE7_STATEMENT_Y_ADJUST), font);
    } else if (data.signingCapacity === 'litigation_friend') {
      drawCheckmark(page, applyOffset(TE7_CHECKBOXES_POS.signingLitigationFriend, 0, TE7_STATEMENT_Y_ADJUST), font);
    }
  }

  const fallbackSignature = applyOffset(TE7_SIGNATURE_POS, 0, TE7_STATEMENT_Y_ADJUST);
  const signatureTarget = isSignaturePositionPlausible(signaturePosition, fallbackSignature)
    ? signaturePosition!
    : fallbackSignature;
  await drawSignature(pdfDoc, page, clampSignaturePosition(signatureTarget, page), data.signatureDataUrl);
  if (options?.debug) {
    drawSignatureDebugOverlay(pdfDoc, page, font, clampSignaturePosition(signatureTarget, page), fallbackSignature);
  }

  return pdfDoc.save();
};

export const generateTeForms = async (
  data: TeFormData,
  options: { includeTE9: boolean; includeTE7: boolean }
): Promise<{ te9?: Uint8Array; te7?: Uint8Array }> => {
  const result: { te9?: Uint8Array; te7?: Uint8Array } = {};

  if (options.includeTE9) {
    result.te9 = await generateTE9Pdf(data);
  }

  if (options.includeTE7) {
    result.te7 = await generateTE7Pdf(data);
  }

  return result;
};

// ============================================================================
// DOWNLOAD HELPER
// ============================================================================

export const downloadPdf = (bytes: Uint8Array, filename: string) => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const toBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};
